/**
 * Frontend copies of the backend secure_proxy whitelist validators.
 *
 * Source of truth (DO NOT modify independently — if these drift, update both):
 *   - erp-integration-api/packages/erp-utils/src/secure-proxy/whitelist.ts
 *   - erp-integration-api/packages/erp-utils/src/secure-proxy/cidr.ts
 *
 * Backend remains authoritative; these run client-side purely to give users
 * instant, inline "this pattern would be rejected" feedback before they
 * hit Save. Error messages must match the backend strings byte-for-byte
 * so the inline error and a server 400 body look identical to staff.
 */

/**
 * Validates a domain pattern. Mirrors backend whitelist.ts:validateDomainPattern.
 *
 * Rules:
 *  - Any pattern NOT starting with "*." is treated as an exact domain and accepted here.
 *  - A wildcard pattern's suffix (chars after "*.") must contain at least 2 labels
 *    (i.e. must include a dot and be non-empty). "*.com" and "*." are rejected.
 *
 * @returns null if valid, or the exact backend error message if invalid.
 */
export function validateDomainPattern(pattern: string): string | null {
  if (!pattern.startsWith('*.')) {
    return null
  }

  const suffix = pattern.slice(2) // e.g. "example.com"

  if (!suffix || !suffix.includes('.')) {
    return `Wildcard pattern "${pattern}" is too broad. Suffix must have at least 2 labels (e.g., "*.example.com")`
  }

  return null
}

/**
 * Validates an array of domain patterns.
 * Returns the first error produced by validateDomainPattern, or null if all are valid.
 */
export function validateDomainPatterns(patterns: string[]): string | null {
  for (const pattern of patterns) {
    const error = validateDomainPattern(pattern)

    if (error) {
      return error
    }
  }

  return null
}

/**
 * Browser-safe IPv4 address validator.
 *
 * The backend uses node's `net.isIPv4`. The browser has no `net`, so we
 * implement the check directly here: four dot-separated decimal octets,
 * each 0-255, with no leading zeros (other than the literal "0").
 */
function isIPv4(value: string): boolean {
  const parts = value.split('.')

  if (parts.length !== 4) {
    return false
  }

  for (const part of parts) {
    if (!/^(0|[1-9][0-9]{0,2})$/.test(part)) {
      return false
    }

    const n = Number(part)

    if (n < 0 || n > 255) {
      return false
    }
  }

  return true
}

/**
 * Validates a CIDR notation string. Mirrors backend cidr.ts:validateCidr.
 *
 * All failure messages start with `Invalid CIDR "${cidr}": `.
 *
 * @returns null if valid, or the exact backend error message if invalid.
 */
export function validateCidr(cidr: string): string | null {
  const slashIndex = cidr.indexOf('/')

  if (slashIndex === -1) {
    return `Invalid CIDR "${cidr}": missing prefix length (e.g., "10.0.0.0/24")`
  }

  const baseIp = cidr.slice(0, slashIndex)
  const prefixStr = cidr.slice(slashIndex + 1)
  const prefixLength = parseInt(prefixStr, 10)

  if (!isIPv4(baseIp)) {
    return `Invalid CIDR "${cidr}": "${baseIp}" is not a valid IPv4 address`
  }

  // Match the backend's strict prefix check: `prefixStr !== String(prefixLength)`
  // rejects trailing junk ("24a") and leading "+"/whitespace that parseInt would tolerate.
  if (
    isNaN(prefixLength) ||
    prefixStr !== String(prefixLength) ||
    prefixLength < 0 ||
    prefixLength > 32
  ) {
    return `Invalid CIDR "${cidr}": prefix length must be 0-32`
  }

  return null
}

/**
 * Validates an array of CIDR strings.
 * Returns the first error produced by validateCidr, or null if all are valid.
 */
export function validateCidrs(cidrs: string[]): string | null {
  for (const cidr of cidrs) {
    const error = validateCidr(cidr)

    if (error) {
      return error
    }
  }

  return null
}
